'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Box, Button, Container, Grid, Typography } from '@mui/material'

// Types
import type { SolutionHeroProps } from '@/types/components/organisms/solution'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const FloatAtom = dynamic(() => import('@/components/atoms/animation/float'))
const FramedImageAtom = dynamic(() => import('@/components/atoms/image/framed'))
const EyebrowTypographyAtom = dynamic(() => import('@/components/atoms/typography/eyebrow'))
const SoonBadgeAtom = dynamic(() => import('@/components/atoms/badge/soon'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const StatCardMolecule = dynamic(() => import('@/components/molecules/solution/stat-card'))

// Page opener: promise, demo button and a photo with floating result cards
const SolutionHeroOrganism = (props: SolutionHeroProps) => {
	// Props
	const { lng, locale, eyebrow, badge, title, subtitle, image, stats, action, secondary, hasSteps } = props

	// Variables
	const primary = action ?? { text: locale.links.requestDemo, href: '/signup' }
	const second = secondary ?? (hasSteps ? { text: locale.common.solutionPage.seeHowItWorks, href: '#how-it-works' } : undefined)

	return (
		<Box sx={{ position: 'relative', overflow: 'hidden', pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 14 } }}>
			{/* Soft background glow */}
			<Box
				sx={{
					position: 'absolute',
					top: -200,
					insetInlineStart: -200,
					width: 600,
					height: 600,
					borderRadius: '50%',
					background: t => `radial-gradient(circle, ${t.palette.primary.light} 0%, transparent 65%)`,
					opacity: 0.7,
					pointerEvents: 'none'
				}}
			/>

			<Container maxWidth="lg" sx={{ position: 'relative' }}>
				<Grid container spacing={{ xs: 6, md: 8 }} sx={{ alignItems: 'center' }}>
					<Grid size={{ xs: 12, md: 6 }}>
						<RevealAtom>
							<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
								<EyebrowTypographyAtom text={eyebrow} />
								{badge && <SoonBadgeAtom label={badge} />}
							</Box>

							<Typography component="h1" sx={{ mt: 3, fontSize: { xs: 32, md: 40 }, fontWeight: 700, lineHeight: 1.45 }}>
								{title}
							</Typography>

							<Typography sx={{ mt: 3, fontSize: { xs: 16, md: 18 }, lineHeight: 2, color: 'text.secondary', maxWidth: 520 }}>{subtitle}</Typography>

							<Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
								<Link href={localizeHref(lng, primary.href)}>
									<PrimaryButtonAtom text={primary.text} color="primary" size="large" sx={{ px: 4 }} />
								</Link>

								{second && (
									<Button
										href={localizeHref(lng, second.href)}
										sx={{
											px: 3,
											py: 1.5,
											borderRadius: 1,
											fontSize: 16,
											fontWeight: 500,
											textTransform: 'inherit',
											color: 'text.primary',
											'&:hover': { bgcolor: 'grey.100' }
										}}
									>
										{second.text}
									</Button>
								)}
							</Box>
						</RevealAtom>
					</Grid>

					<Grid size={{ xs: 12, md: 6 }}>
						<RevealAtom delay={0.15} offset={40}>
							<Box sx={{ position: 'relative', mx: { xs: 2, md: 0 } }}>
								<FramedImageAtom src={image} alt={title} height={{ xs: 380, md: 560 }} radius={8} sizes="(max-width: 900px) 100vw, 560px" priority />

								{stats?.map((stat, index) => (
									<Box
										key={stat.label}
										sx={{
											position: 'absolute',
											width: { xs: 180, md: 220 },
											...(index === 0
												? { top: { xs: 24, md: 48 }, insetInlineStart: { xs: -16, md: -48 } }
												: { bottom: { xs: 24, md: 56 }, insetInlineEnd: { xs: -16, md: -40 } })
										}}
									>
										<FloatAtom delay={index * 1.2}>
											<StatCardMolecule lng={lng} variant="glass" {...stat} />
										</FloatAtom>
									</Box>
								))}
							</Box>
						</RevealAtom>
					</Grid>
				</Grid>
			</Container>
		</Box>
	)
}

export default SolutionHeroOrganism
